import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CheckCircle,
  XCircle,
  Info,
  AlertTriangle,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { slideUp } from '@/lib/motion'

// ============================================
// TYPES
// ============================================

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastData {
  id: string
  type: ToastType
  title: string
  description?: string
  icon?: LucideIcon
  duration?: number
  /** Extra accent class overrides (e.g. 'border-gold/30') */
  accentClass?: string
}

interface ToastContextValue {
  toast: (data: Omit<ToastData, 'id'>) => void
  /** Convenience: deposit success toast */
  toastDeposit: (amount: string) => void
  /** Convenience: withdrawal submitted toast */
  toastWithdrawal: (amount: string) => void
  /** Convenience: room created toast */
  toastRoomCreated: (roomCode: string) => void
  /** Convenience: jackpot win toast */
  toastJackpot: (amount: string) => void
  dismiss: (id: string) => void
}

// ============================================
// CONTEXT
// ============================================

const ToastContext = createContext<ToastContextValue | null>(null)

// ============================================
// TOAST ITEM
// ============================================

const typeConfig: Record<
  ToastType,
  { icon: LucideIcon; borderClass: string; iconClass: string; barClass: string }
> = {
  success: {
    icon: CheckCircle,
    borderClass: 'border-emerald-500/30',
    iconClass: 'text-emerald-400',
    barClass: 'bg-emerald-500',
  },
  error: {
    icon: XCircle,
    borderClass: 'border-destructive/30',
    iconClass: 'text-destructive',
    barClass: 'bg-destructive',
  },
  info: {
    icon: Info,
    borderClass: 'border-blue-500/30',
    iconClass: 'text-blue-400',
    barClass: 'bg-blue-500',
  },
  warning: {
    icon: AlertTriangle,
    borderClass: 'border-amber-500/30',
    iconClass: 'text-amber-400',
    barClass: 'bg-amber-500',
  },
}

interface ToastItemProps {
  data: ToastData
  onDismiss: (id: string) => void
}

function ToastItem({ data, onDismiss }: ToastItemProps) {
  const { type, title, description, icon, duration = 3000, accentClass } = data
  const config = typeConfig[type]
  const Icon = icon ?? config.icon
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onDismiss(data.id)
    }, duration)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [data.id, duration, onDismiss])

  return (
    <motion.div
      layout
      variants={slideUp}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={cn(
        'relative w-full max-w-sm overflow-hidden rounded-2xl',
        'glass-card-elevated',
        accentClass ?? config.borderClass
      )}
      onClick={() => onDismiss(data.id)}
      role="alert"
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div
          className={cn(
            'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
            `${config.iconClass} bg-current/10`
          )}
          style={{
            backgroundColor: `color-mix(in srgb, currentColor 10%, transparent)`,
          }}
        >
          <Icon className={cn('w-5 h-5', config.iconClass)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-sm font-semibold text-foreground truncate">
            {title}
          </p>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground/5">
        <motion.div
          className={cn('h-full', config.barClass)}
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: duration / 1000, ease: 'linear' }}
        />
      </div>
    </motion.div>
  )
}

// ============================================
// PROVIDER
// ============================================

export interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([])
  const idCounter = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback((data: Omit<ToastData, 'id'>) => {
    const id = `toast-${++idCounter.current}-${Date.now()}`
    setToasts((prev) => [...prev, { ...data, id }])
  }, [])

  // Convenience methods for pre-built toast patterns

  const toastDeposit = useCallback(
    (amount: string) => {
      toast({
        type: 'success',
        title: '儲值成功',
        description: `$${amount} 已存入您的錢包`,
        accentClass: 'border-emerald-500/30',
      })
    },
    [toast]
  )

  const toastWithdrawal = useCallback(
    (amount: string) => {
      toast({
        type: 'info',
        title: '提款已提交',
        description: `$${amount} 提款申請正在處理中`,
        accentClass: 'border-blue-500/30',
      })
    },
    [toast]
  )

  const toastRoomCreated = useCallback(
    (roomCode: string) => {
      toast({
        type: 'success',
        title: '房間已創建',
        description: `房間代碼: ${roomCode}`,
        accentClass: 'border-gold/30',
        icon: CheckCircle,
      })
    },
    [toast]
  )

  const toastJackpot = useCallback(
    (amount: string) => {
      toast({
        type: 'success',
        title: '恭喜中獎！',
        description: `累積獎金 $${amount}`,
        icon: Sparkles,
        duration: 5000,
        accentClass: 'border-gold/40',
      })
    },
    [toast]
  )

  const value: ToastContextValue = {
    toast,
    toastDeposit,
    toastWithdrawal,
    toastRoomCreated,
    toastJackpot,
    dismiss,
  }

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast container - bottom center above tab bar */}
      <div className="fixed bottom-20 left-0 right-0 z-[150] flex flex-col items-center gap-2 px-4 pointer-events-none">
        <AnimatePresence mode="popLayout">
          {toasts.map((t) => (
            <div key={t.id} className="pointer-events-auto w-full flex justify-center">
              <ToastItem data={t} onDismiss={dismiss} />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}

// ============================================
// HOOK
// ============================================

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>')
  }
  return ctx
}
